/**
 * New Year bookmarks — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearBookmarksPack: ToilePack = {
    slug: 'new-year-bookmarks',
    category: 'season',
    name: 'New Year bookmarks',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'firework', label: 'Firework', subject: 'A sheet of four tall bookmarks each with a firework running the length and a tassel hole, the foot of each lettered exactly "Cheers"'},
        {key: 'tracker', label: 'Reading tracker', subject: 'A sheet of four tall bookmarks each lettered "Books this year" with twelve small blank boxes down the length, dashed cut lines'},
        {key: 'greeting', label: 'Greeting', subject: 'A sheet of four tall bookmarks each lettered "Happy New Year" down the length with stars, a tassel hole, dashed cut lines'},
    ],
    styles: [
        {key: 'glam', label: 'Black and gold', style: 'black-and-gold new year printable, confetti borders, elegant serif lettering, print-ready', palette: 'Black, gold and champagne cream', framing: 'Laid out on a dark page with a gold border and a margin'},
        {key: 'confetti', label: 'Confetti party', style: 'bright confetti party printable, streamers, rounded bold lettering, print-ready', palette: 'Gold, pink, teal and white', framing: 'Laid out on a vertical page with a confetti border and a white margin'},
        {key: 'minimal', label: 'Minimal', style: 'minimal new year printable, thin lines, generous white space, print-ready', palette: 'Black and gold on white', framing: 'Laid out on a clean page with wide margins'},
    ],
    directions: ['with a champagne-glass motif instead', 'for a twelve-month reading log', 'in a shorter, wider shape'],
    directionHint: 'try: a champagne-glass motif instead',
    seo: {
        title: 'New Year Bookmarks: Firework, Tracker & Greeting',
        metaDescription: 'Nine New Year bookmarks — a firework design, a reading tracker, and a greeting set, four to a sheet — in black-and-gold, confetti, minimal.',
        h1: 'New Year bookmarks: firework, reading tracker, greeting',
        tagline: 'Nine sheets, three bookmark designs, three styles each — four per sheet, cut lines drawn.',
        intro: 'Three New Year bookmark sheets — a firework design, a twelve-month reading tracker, and a lettered greeting — each printing four tall bookmarks per page, in black-and-gold, confetti-party, and minimal styles. Ask for a different motif or a book-club version, and the same bookmark grammar carries it.',
        useCases: [
            'a New Year reading-resolution gift',
            'a classroom party favor',
            'a library January display',
            'a book-club welcome gift',
        ],
        faq: [
            {q: 'How many bookmarks are on one sheet?', a: 'Four, arranged in a column with dashed cut lines between them.'},
            {q: 'What size do these print at?', a: 'Each sheet is a single vertical page, close to US letter or A4 proportions.'},
            {q: 'Can I get a different motif or a book-club version?', a: 'Yes — the direction field takes any variation, including a different motif.'},
            {q: 'What are the three styles?', a: 'Black-and-gold, bright confetti-party, and minimal thin-line.'},
        ],
    },
}
